
import { namespace } from 'vuex-class';
import { Vue, Component, Prop, Watch, Emit, Model } from 'vue-property-decorator';

import { ClipsEvent } from './types';
import TitleHelper from '@/helpers/title-helper';
import { VideoAdsHelper } from '@/helpers/video-ads-helper';
import { TitleDetailsNode } from '@/interfaces/title-details-graphql';
import type { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';

import { WebLocale } from '@/enums/web-locale';

const BackdropCarousel = () =>
	import(/* webpackChunkName: "backdrop-carousel" */ '@/components/details/backdrops/BackdropCarousel.vue');
const BackdropTrailer = () =>
	import(/* webpackChunkName: "backdrop-trailer" */ '@/components/details/backdrops/BackdropTrailer.vue');

const Language = namespace('language');
const Routing = namespace('routing');
const User = namespace('user');

@Component({
	name: 'TitleBackdrops',
	components: {
		BackdropCarousel,
		BackdropTrailer,
	},
})
export default class TitleBackdrops extends Vue {
	@Prop({ required: true }) title: TitleDetailsNode;
	@Prop({ default: () => [] }) additionalContexts: SnowplowContext[];

	@Model(ClipsEvent.MODEL, { default: null }) readonly activeClipId!: string | null;

	@Language.State webLocale: WebLocale;
	@Routing.State activeRoute: any;

	@User.Getter isLoggedIn: () => boolean;

	get titleName() {
		return this.title.content.title;
	}

	get titleClips() {
		return TitleHelper.getBackdropClips(this.title);
	}

	get hasBackdropContent(): boolean {
		const hasCarouselImages = this.title.content.backdrops.length > 0;
		const hasTrailerClips = this.titleClips.length > 0;

		return hasCarouselImages || hasTrailerClips;
	}

	get backdropClasses() {
		return {
			backdrops__carousel: !this.shouldShowTrailerBackdrop && this.shouldShowCarouselBackdrop,
			'backdrops--trailer-playing': !!this.activeClipId,
		};
	}

	get shouldShowTrailerBackdrop(): boolean {
		return !this.isLoggedIn() && !!this.title.content.title && this.titleClips.length > 0;
	}

	get shouldShowCarouselBackdrop(): boolean {
		return this.imageUrls.length > 0;
	}

	get imageUrls(): String[] {
		const pathExtension = this.title.content.fullPath
			? `/${this.title.content.fullPath.substring(this.title.content.fullPath.lastIndexOf('/') + 1)}`
			: '';

		return this.title.content.backdrops.map(backdrop => backdrop.backdropUrl + pathExtension);
	}

	mounted() {
		if (this.activeClipId) {
			this.emitTrailerPlayingModel(null);
		}
		this.loadVideoAd();
	}

	@Watch('activeRoute')
	onRouteChange() {
		this.emitTrailerPlayingModel(null);
		this.loadVideoAd();
	}

	handleBackdropTrailer(activeClipId: string | null) {
		this.emitTrailerPlayingModel(activeClipId);
		this.emitBackdropClicked();
	}

	@Emit(ClipsEvent.MODEL)
	emitTrailerPlayingModel(activeClipId: string | null) {
		return activeClipId;
	}

	@Emit('backdrop-clicked')
	emitBackdropClicked() {}

	loadVideoAd() {
		if (!this.isLoggedIn()) VideoAdsHelper.loadVideoAd(this.webLocale);
	}
}
